import React, { useState } from 'react';
import { X, Calendar, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface RescheduleModalProps {
  isOpen: boolean;
  quoteId: number | string;
  currentDate?: string;
  currentTime?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  quoteId,
  currentDate,
  currentTime,
  onClose,
  onSuccess
}) => {
  const [newDate, setNewDate] = useState(currentDate || '');
  const [newTime, setNewTime] = useState(currentTime || '');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleReschedule = async () => {
    if (!newDate || !newTime) {
      toast({
        title: 'Missing Information',
        description: 'Please select both a date and time.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.request(`/quotes/${quoteId}/reschedule`, 'PUT', {
        preferred_service_date: newDate,
        preferred_service_time: newTime,
        reschedule_reason: rescheduleReason
      });

      if (response.success) {
        toast({
          title: 'Rescheduled Successfully',
          description: `Your service has been rescheduled to ${new Date(newDate).toLocaleDateString()} at ${newTime}`,
        });
        onSuccess();
        handleClose();
      } else {
        toast({
          title: 'Reschedule Failed',
          description: response.message || 'Could not reschedule your service.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Reschedule error:', error);
      toast({
        title: 'Error',
        description: 'Failed to reschedule. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewDate(currentDate || '');
    setNewTime(currentTime || '');
    setRescheduleReason('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold">Reschedule Service</h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Current Schedule Info */}
            {currentDate && currentTime && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Current Schedule</p>
                <p className="font-semibold text-blue-900">
                  {new Date(currentDate).toLocaleDateString()} at {currentTime}
                </p>
              </div>
            )}

            {/* New Date */}
            <div>
              <Label htmlFor="new-date" className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4" />
                New Date
              </Label>
              <Input
                id="new-date"
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* New Time */}
            <div>
              <Label htmlFor="new-time" className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4" />
                New Time
              </Label>
              <Input
                id="new-time"
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
              />
            </div>

            {/* Reason (optional) */}
            <div>
              <Label htmlFor="reason">Reason for Rescheduling (Optional)</Label>
              <textarea
                id="reason"
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="Let us know why you're rescheduling..."
                className="w-full p-2 border rounded-md text-sm mt-2"
                rows={3}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t bg-gray-50">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReschedule}
              disabled={isLoading || !newDate || !newTime}
              className="flex-1"
            >
              {isLoading ? 'Rescheduling...' : 'Confirm Reschedule'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RescheduleModal;
